import { useRef, useState, useEffect } from "react"
import mapboxgl from 'mapbox-gl'
import Sheet from 'react-modal-sheet'
import AutocompleteInput from "../../shared/components/AutocompleteInput"
import GoogleRepository from "../../repository/GoogleRepository"

export default function LocationPickerModal(
    {
        existingPlace,
        isLocationPickerModalOpen,
        onLocationPickerModalDismiss,
        onLocationSelected
    }
) {
    const mapRef = useRef()
    const [currentMap, setCurrentMap] = useState(null)
    const [searchValue, setSearchValue] = useState("")
    const [searchTerm, setSearchTerm] = useState('')
    const [autocompleteLoadingText, setAutocompleteLoadingText] = useState(null)
    const [geocodedPlace, setGeocodedPlace] = useState(null)
    const [searchPredictions, setSearchPredictions] = useState([])
    const [selectedPlace, setSelectedPlace] = useState(existingPlace)
    const [appMapConfig, setAppMapConfig] = useState(null)

    useEffect(() => {
        const googleRepository = new GoogleRepository()
  
        async function getPlacesAutocomplete(input) {
          const places = await googleRepository.getPlacesAutocomplete(input)
          console.log(places)
          setSearchPredictions(
            places.predictions.map((prediction) => {
              return {
                label: prediction.structured_formatting.main_text,
                secondaryLabel: prediction.structured_formatting.secondary_text,
                key: prediction.place_id,
                link: "#"
              }
            })
          )
          setAutocompleteLoadingText(null)
        }
    
        const delayDebounceFn = setTimeout(() => {
          console.log(searchTerm)
          if (searchTerm) {
            getPlacesAutocomplete(searchTerm)
          }
        }, 1500)
    
        return () => clearTimeout(delayDebounceFn)
      }, [searchTerm])

    useEffect(() => {
        if (currentMap && geocodedPlace) {
            currentMap.easeTo({
                center: [geocodedPlace.coordinates.longitude, geocodedPlace.coordinates.latitude],
                zoom: appMapConfig.zoom
            })
            setSelectedPlace({
                label: geocodedPlace.label,
                coordinates: {
                    latitude: geocodedPlace.coordinates.latitude,
                    longitude: geocodedPlace.coordinates.longitude
                }
            })
        }
    }, [geocodedPlace])


    async function getPlaceDetails(placeId) {
        const googleRepository = new GoogleRepository()
        const placeDetails = await googleRepository.getPlaceDetails(placeId)
        console.log(placeDetails)
        setGeocodedPlace({
            label: placeDetails.result.name,
            coordinates: {
                latitude: placeDetails.result.geometry.location.lat,
                longitude: placeDetails.result.geometry.location.lng
            }
        })
    }

    function handlePlaceItemSelected(item) {
        console.log(`handle place item selected ${JSON.stringify(item)}`)
        // setSelectedPlace(item)
        getPlaceDetails(item.key)
    }

    function onSaveLocationClick() {
        console.log(`on save location click ${searchValue}`)
        onLocationSelected(selectedPlace.coordinates)

    }

    function handlePlaceInputChange(event) {
        setAutocompleteLoadingText("Loading ...")
        setGeocodedPlace(null)
        setSearchPredictions([])
        const searchString = event.target.value
        setSearchTerm(searchString)
    }

    useEffect(() => {
        console.log("map ref is rendered")
        let mapStyle
        if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
            mapStyle = 'mapbox://styles/mapbox/dark-v11'
        } else {
            mapStyle = 'mapbox://styles/mapbox/streets-v12'
        }
        const map = new mapboxgl.Map({
            container: 'map-container',
            style: mapStyle,
        });

        map.on('render', function () {
            map.resize();
        });
        map.on('load', function() {
          setCurrentMap(map);
        })
        map.on('moveend', () => {
            console.log(`move end`)
            const mapCenter = map.getCenter()
            console.log(`on map drag end ${mapCenter} ${mapCenter.lat == geocodedPlace?.coordinates?.latitude} ${mapCenter.lng == geocodedPlace?.coordinates?.longitude}`);
            setAppMapConfig({
                center: mapCenter,
                zoom: map.getZoom()
            })
            if (mapCenter.lat !== geocodedPlace?.coordinates?.latitude || mapCenter.lng !== geocodedPlace?.coordinates?.longitude) {
                setSelectedPlace({
                    coordinates: {
                        latitude: mapCenter.lat,
                        longitude: mapCenter.lng
                    }
                })
            }
        })
        map.on('zoomend', () => {
            const mapCenter = map.getCenter()
            console.log(`on map zoom end ${mapCenter}`);
            setAppMapConfig({
                center: mapCenter,
                zoom: map.getZoom()
            })
        })

        if (existingPlace) {
            map.easeTo({
                center: [existingPlace.coordinates.longitude, existingPlace.coordinates.latitude],
                zoom: 17
            })
        }
    }, [mapRef]);

    useEffect(() => {
        if (existingPlace) {
            setSelectedPlace(existingPlace)
            if (currentMap) {
                currentMap.easeTo({
                    center: [existingPlace.coordinates.longitude, existingPlace.coordinates.latitude],
                    zoom: appMapConfig.zoom
                })
            }
        }
    }, [existingPlace])

    function onLatitudeInputChange(event) {
        console.log(`on latitude input change ${event.target.value}`)
        const updatedCoordinates = { ...selectedPlace.coordinates, latitude: event.target.value ? parseFloat(event.target.value) : 1.1}
        console.log(`updated coordinates ${JSON.stringify(updatedCoordinates)}`)
        setSelectedPlace({ ...selectedPlace, coordinates: updatedCoordinates })
    }

    function onLongitudeInputChange(event) {
        console.log(`on latitude input change ${event.target.value}`)
        const updatedCoordinates = { ...selectedPlace.coordinates, longitude: event.target.value ? parseFloat(event.target.value) : 101.0 }
        console.log(`updated coordinates ${JSON.stringify(updatedCoordinates)}`)
        setSelectedPlace({ ...selectedPlace, coordinates: updatedCoordinates })
    }

    return (
        <Sheet
        isOpen={isLocationPickerModalOpen} 
        onClose={() => {onLocationPickerModalDismiss()}} 
        onOpenEnd={() => {}} 
        detent="content-height"
        className="z-50"
        disableDrag={true}
        >
            <Sheet.Container>
            <Sheet.Content className="bg-base-300">
            <Sheet.Scroller draggableAt="both">
            <div className="relative w-full h-full">

                    <div className="text-base px-4 pb-4 pt-8 flex flex-row items-center gap-2">
                    <span className="font-bold">↕↔ Drag the following map / 🔎 search in the box below </span> to select a location
                    </div>
                    <div className="flex w-full gap-2 px-4 pb-4">
                        <AutocompleteInput 
                        placeholder={"Type here ..."}
                        onValueChange={handlePlaceInputChange}
                        items={ searchPredictions }
                        value={ geocodedPlace?.label ?? searchTerm }
                        onItemSelected={handlePlaceItemSelected}
                        loadingText={autocompleteLoadingText}
                        />
                    </div>
                    <div className="px-4 pb-4">
                        <div className="text-xl p-4 flex flex-row items-center gap-2 bg-accent text-accent-content rounded-md p-2 w-max">
                        <span className="font-titan">Selected Coordinates:</span>
                        {
                            selectedPlace?.label && (
                                <span className="text-accent-content">
                                    {selectedPlace.label}
                                </span>
                            )
                        }
                        {
                            selectedPlace?.coordinates && (
                                <div className="flex flex-row items-center gap-2">
                                    <input className={`input input-bordered flex-1 rounded-md z-1 bg-accent`} value={selectedPlace.coordinates.latitude} placeholder="Latitude" onChange={onLatitudeInputChange} />
                                    <span>,</span>
                                    <input className={`input input-bordered flex-1 rounded-md z-1 bg-accent`} value={selectedPlace.coordinates.longitude} placeholder="Longitude" onChange={onLongitudeInputChange} />
                                </div>
                            )
                        }
                        </div>
                    </div>
                    <div className="col-span-1 h-[300px] relative">
                        <div className={`w-full h-full`} id='map-container'>
                        </div>
                        <div className="absolute text-2xl text-base-content left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">🟢</div>
                    </div>

                    <div className="modal-action w-full text-right px-4 py-4 ">
                        <button className="btn btn-success text-success-content mr-2 normal-case" onClick={
                            () => {
                                onSaveLocationClick()
                            }
                            } >
                            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path className="stroke-success-content" d="M8 20H6C4.89543 20 4 19.1046 4 18V6C4 4.89543 4.89543 4 6 4H9M8 20V14C8 13.4477 8.44772 13 9 13H15C15.5523 13 16 13.4477 16 14V20M8 20H16M16 20H18C19.1046 20 20 19.1046 20 18V8.82843C20 8.29799 19.7893 7.78929 19.4142 7.41421L16.5858 4.58579C16.2107 4.21071 15.702 4 15.1716 4H15M15 4V7C15 7.55228 14.5523 8 14 8H10C9.44772 8 9 7.55228 9 7V4M15 4H9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>    
                            Save
                        </button>

                        <button className="btn btn-circle btn-soft" onClick={
                                () => {
                                    onLocationPickerModalDismiss()
                                }
                                }>
                            <svg className="fill-base-content w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M19.207 6.207a1 1 0 0 0-1.414-1.414L12 10.586 6.207 4.793a1 1 0 0 0-1.414 1.414L10.586 12l-5.793 5.793a1 1 0 1 0 1.414 1.414L12 13.414l5.793 5.793a1 1 0 0 0 1.414-1.414L13.414 12l5.793-5.793z"/></svg>
                        </button>
                    </div>
            </div>
            </Sheet.Scroller>
            </Sheet.Content>
            </Sheet.Container>
            <Sheet.Backdrop onTap={(_) => {
                onLocationPickerModalDismiss()
            }}/>
        </Sheet>
    )
}